import { Badge, Button, Card, CardHeader, Drawer, Table, formatDate, useToast, type Column } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { runMonthAction, type DraftSummary, type MonthAction, type MonthStatus, type Payslip } from "../../api/payroll";
import { downloadCsv } from "../../api/reports";
import { useServices } from "../../app/services";
import { NotFound } from "../../app/NotFound";
import { useMonth, useProfiles, nameMap } from "./hooks";
import { Empty, Gate, PageHead, TableSkeleton, css as s, errorText, isMonth, monthLabel, shiftMonth, useScope } from "./kit";

const STEPS: Array<{ key: MonthStatus; label: string }> = [{ key: "draft", label: "Draft" }, { key: "approved", label: "Approved" }, { key: "locked", label: "Locked" }];
const ORDER: Array<MonthStatus | null> = [null, "draft", "approved", "locked"];

export function Stepper({ status }: { status: MonthStatus | null }) {
  const at = ORDER.indexOf(status);
  return (
    <ol className={s.stepper} aria-label="Month status">
      {STEPS.map((st, i) => {
        const idx = i + 1;
        const state = idx < at || (status === "locked" && idx === at) ? "done" : idx === at ? "current" : "todo";
        return <li key={st.key} className={s.step} data-state={state} aria-current={idx === at ? "step" : undefined}><i>{idx}</i>{st.label}</li>;
      })}
    </ol>
  );
}

export const statusTone = (st: MonthStatus | null) => (st === "locked" ? "ok" : st === "approved" ? "info" : st === "draft" ? "warn" : "neutral");

export function MonthPage() {
  const { month } = useParams();
  const sc = useScope();
  if (!isMonth(month)) return <NotFound />;
  // Keyed: a draft summary, an error or a half-typed lock confirmation belongs to one month of one branch.
  return <MonthBody key={`${month}:${sc.monthBranchId}`} month={month} />;
}

function MonthBody({ month }: { month: string }) {
  const sc = useScope();
  const { client, session } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const q = useMonth(sc, month);
  const profiles = useProfiles(sc);
  const names = nameMap(profiles.data?.profiles);
  const [lockOpen, setLockOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [summary, setSummary] = useState<Partial<DraftSummary> | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const status = q.data?.month.status ?? null;
  // Draft, approve and lock are separate levers (D57), as each POST /payroll/months/:month/* route checks.
  const canDraft = sc.ctx.has("payroll.draft");
  const canApprove = sc.ctx.has("payroll.approve");
  const canLock = sc.ctx.has("payroll.lock");
  const canExport = sc.ctx.has("exports.payroll");

  const act = useMutation({
    mutationFn: (a: MonthAction) => runMonthAction(client, month, sc.monthBranchId!, a),
    onSuccess: async (r, a) => {
      setActionError(null);
      if (a === "draft") setSummary(r);
      else setSummary(null);
      if (a === "lock") { setLockOpen(false); setTyped(""); }
      toast.show(a === "draft" ? "Draft built" : a === "approve" ? "Month approved" : "Month locked");
      await qc.invalidateQueries({ queryKey: ["payroll"] });
    },
    onError: (e) => setActionError(errorText(e)),
  });

  const cols: Array<Column<Payslip>> = [
    { key: "who", header: "Staff", cell: (p) => <span className={s.truncate} title={names.get(p.staffId) ?? p.staffId}>{names.get(p.staffId) ?? p.staffId}</span> },
    { key: "gross", header: "Gross", align: "end", cell: (p) => <span className={s.num}>{sc.money(p.gross)}</span> },
    { key: "ded", header: "Deductions", align: "end", cell: (p) => <span className={s.num}>{sc.money(p.deductions)}</span> },
    { key: "net", header: "Net", align: "end", cell: (p) => <b className={s.num}>{sc.money(p.net)}</b> },
    { key: "paid", header: "Paid", cell: (p) => (p.paidOn ? <Badge tone="ok" dot>{formatDate(p.paidOn, sc.locale, "short")}</Badge> : <Badge>Unpaid</Badge>) },
  ];

  const exportCsv = async () => {
    setExportError(null);
    try { await downloadCsv(session, "/payroll/export", { month, branchId: sc.monthBranchId }, `payroll-${month}.csv`); } catch (e) { setExportError(errorText(e)); }
  };

  return (
    <div className={s.page}>
      <PageHead title={`Payroll · ${monthLabel(month, sc.locale)}`} sub={<Link to="/payroll">All months</Link>}>
        <Button as={Link} to={`/payroll/${shiftMonth(month, -1)}`} size="sm" aria-label="Previous month">‹ {monthLabel(shiftMonth(month, -1), sc.locale)}</Button>
        <Button as={Link} to={`/payroll/${shiftMonth(month, 1)}`} size="sm" aria-label="Next month">{monthLabel(shiftMonth(month, 1), sc.locale)} ›</Button>
        {canExport && status ? <Button size="sm" onClick={() => void exportCsv()}>Export CSV</Button> : null}
      </PageHead>

      {!sc.monthBranchId ? <Card><Empty icon="cash" title="Choose a branch" description="Payroll runs one branch at a time. Pick a branch in the switcher." /></Card> : (
        <Card aria-label="Month status">
          <CardHeader title="Month" subtitle="Draft, approve, then lock. Locked months never change." action={<Badge tone={statusTone(status)} dot>{status ? STEPS.find((x) => x.key === status)!.label : "Not started"}</Badge>} />
          <Gate query={q} what="this month" skeleton={<TableSkeleton rows={3} label="Loading month" />}>
            {(d) => (
              <>
                <Stepper status={d.month.status} />
                {actionError ? <p className={s.formErr} role="alert">{actionError}</p> : null}
                {exportError ? <p className={s.formErr} role="alert">{exportError}</p> : null}
                <div className={s.strip}>
                  <div><small>Held commission outstanding</small><b>{sc.money(d.heldOutstanding)}</b></div>
                  <div><small>Commission events not posted</small><b>{d.unpostedEvents}</b></div>
                  <div><small>Payslips</small><b>{d.payslips.length}</b></div>
                </div>
                <div className={s.body}>
                  <div className={s.actions}>
                    {canDraft && (d.month.status === null || d.month.status === "draft") ? <Button variant={d.month.status ? "default" : "primary"} busy={act.isPending && act.variables === "draft"} busyLabel="Building…" disabled={act.isPending} onClick={() => act.mutate("draft")}>{d.month.status ? "Rebuild draft" : "Build draft"}</Button> : null}
                    {canApprove && d.month.status === "draft" ? <Button variant="primary" busy={act.isPending && act.variables === "approve"} busyLabel="Approving…" disabled={act.isPending} onClick={() => act.mutate("approve")}>Approve month</Button> : null}
                    {canLock && d.month.status === "approved" ? <Button variant="primary" disabled={act.isPending} onClick={() => setLockOpen(true)}>Lock month</Button> : null}
                  </div>
                  {summary ? <DraftNotes summary={summary} names={names} /> : null}
                </div>
                {d.payslips.length === 0 ? (
                  <Empty icon="cash" title="No payslips yet" description={d.month.status === null ? "Build the draft to calculate payslips for your staff." : "Nothing was calculated. Check that staff have salary terms."} action={sc.ctx.has("payroll.profiles_edit") ? <Button as={Link} to="/payroll?tab=profiles">Salary terms</Button> : undefined} />
                ) : (
                  <Table label="Payslips" columns={cols} rows={d.payslips} rowKey={(p) => p.id} onRowClick={(p) => nav(`/payroll/payslips/${p.id}`)} minWidth={560} summary={`${d.payslips.length} payslips`} />
                )}
              </>
            )}
          </Gate>
        </Card>
      )}

      <Drawer open={lockOpen} onOpenChange={(o) => { setLockOpen(o); if (!o) setTyped(""); }} title={`Lock ${month}?`} description="Locking cannot be undone from the app. Corrections go on a later month as an adjustment."
        footer={<><Button onClick={() => setLockOpen(false)}>Cancel</Button><Button variant="primary" disabled={typed !== month} busy={act.isPending && act.variables === "lock"} busyLabel="Locking…" onClick={() => act.mutate("lock")}>Lock month</Button></>}>
        <div className={s.drawerPad}>
          <label className={s.field}><span>Type {month} to confirm</span><input className={s.input} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" aria-label={`Type ${month} to confirm`} /></label>
          {actionError ? <p className={s.formErr} role="alert">{actionError}</p> : null}
        </div>
      </Drawer>
    </div>
  );
}

function DraftNotes({ summary, names }: { summary: Partial<DraftSummary>; names: Map<string, string> }) {
  const problems = summary.problems ?? [];
  const neg = summary.negativeNet ?? [];
  const noProfile = (summary.noProfile ?? []) as Array<unknown>;
  if (!problems.length && !neg.length && !noProfile.length) return <p className={s.note}>Draft built for {summary.payslips ?? 0} staff. No problems found.</p>;
  return (
    <ul className={s.rows} aria-label="Draft notes">
      {problems.map((p, i) => <li key={i} className={s.formErr}>{p.name}: {p.code}</li>)}
      {neg.map((id) => <li key={id} className={s.note}>{names.get(id) ?? id}: net pay is negative.</li>)}
      {noProfile.length ? <li className={s.note}>{noProfile.length} staff have no salary terms and were left out.</li> : null}
    </ul>
  );
}
